import { AuthenticationType, AuthorizationStatus } from './types';
import { embeddedBundle } from './locale/embeddedI18N';

export const versionInfo = { major: 0, minor: 0, patch: 0 };

type authState = {
  statusCbs: Map<string, ((status: AuthorizationStatus) => void)[]>;
  authMap: Map<string, AuthorizationInfo>;
  userIdMap: Map<string, string>;
};

type globalInterface = {
  versionInfo: { major: number; minor: number; patch: number };
  onStatusChange: (
    server: string,
    cb: (status: AuthorizationStatus) => void,
    authenticationType?: AuthenticationType
  ) => void;
  addConnection: (server: string, authenticationType: AuthenticationType) => AuthorizationStatus;
  getValue: (server: string) => AuthorizationStatus | undefined;
  deauthorize: (server: string, authenticationType?: AuthenticationType) => void;
  authorize: (server: string, authenticationType?: AuthenticationType) => void;
  activate: () => void;
  deactivate: () => void;
  getKey: (server: string, auth?: AuthenticationType) => string;
  // Testing only
  _removeServer: (server: string, authenticationType?: AuthenticationType) => void;
  // internal, for upgrades
  _getState: () => authState;
};

type AuthorizationInfo = AuthorizationStatus & {
  guestType?: 'credential' | 'token';
  serverHack?: boolean;
};

let statusCbs = new Map<string, ((status: AuthorizationStatus) => void)[]>();
let authMap = new Map<string, AuthorizationInfo>();
let userIdMap = new Map<string, string>();
let windowFocusCb: (() => void) | undefined;

function getKey(server: string, auth?: AuthenticationType) {
  if (auth === 'guest-token') {
    return `${auth}:${server}`;
  }
  return server;
}

export function getAuthUrl(server: string) {
  return `${server}/SASVisualAnalytics/logon/index.html`;
}

export function setState(state: authState) {
  statusCbs = new Map(state.statusCbs);
  authMap = new Map(state.authMap);
  userIdMap = new Map(state.userIdMap);
}

async function updateUserId(server: string) {
  const userResp = await fetch(`${server}/identities/users/@currentUser`, {
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'include',
  });
  if (!userResp.ok) {
    throw new Error('[authorization] Unable to update user, invalid identities response');
  }
  try {
    const userJson = await userResp.json();
    userIdMap.set(server, userJson.id);
  } catch (e) {
    throw new Error('[authorization] Unable to update user, invalid identities json response');
  }
}

async function performGuestLogon(server: string) {
  const reportsResp = await fetch(`${server}/reports/?sso_guest=true`, {
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'include',
  });
  if (!reportsResp.ok) {
    throw new Error('invalid logon response');
  }
}

async function getGuestToken(server: string) {
  const resp = await fetch(`${server}/SASLogon/oauth/token`, {
    method: 'POST',
    credentials: 'omit',
    headers: {
      // eslint-disable-next-line @sas/i18n/jsx-to-localize
      Authorization: `Basic ${btoa('sas.mobilebi:')}`,
      Accept: 'application/json, */*',
    },
    body: new URLSearchParams('grant_type=guest'),
  });

  if (!resp.ok) {
    return [undefined, '[Authorization]: Failed to fetch guest token.'] as const;
  }
  let token: string | undefined;
  try {
    token = (await resp.json())?.access_token || undefined;
    // eslint-disable-next-line no-empty
  } catch {}

  if (!token) {
    return [undefined, '[Authorization]: no guest token in response.'] as const;
  }
  return [token, undefined] as const;
}

async function initGuestToken(server: string) {
  const [token, error] = await getGuestToken(server);
  if (error) {
    console.error(error);
    updateAuthMapValue(server, 'guest-token', {
      status: 'initalizing',
      error: embeddedBundle['SASServer.logonError.msg'](),
    });
    return;
  }
  updateAuthMapValue(server, 'guest-token', {
    status: 'authorized',
    token: token,
  });
}

function updateAuthMapValue(
  server: string,
  authenticationType: AuthenticationType | undefined,
  value: Partial<AuthorizationInfo>
) {
  const authInfo = authMap.get(getKey(server, authenticationType));
  if (!authInfo) {
    throw new Error('No auth found');
  }
  setAuthMapValue(server, authenticationType, {
    ...authInfo,
    ...value,
  });
}

function setAuthMapValue(
  server: string,
  authenticationType: AuthenticationType | undefined,
  value: AuthorizationInfo
) {
  authMap.set(getKey(server, authenticationType), value);
  const { guestType, serverHack: reportsHack, ...status } = value;
  for (const cb of statusCbs.get(server) || []) {
    cb(status);
  }
}

const authInterfaceImpl: globalInterface & {
  _logonAsGuest: (server: string) => Promise<void>;
} = {
  versionInfo,
  getKey,
  onStatusChange(server, cb, authenticationType?: AuthenticationType) {
    const cbs = statusCbs.get(getKey(server, authenticationType)) || [];
    cbs.push(cb);
    statusCbs.set(server, cbs);
  },
  getValue(server, tokenType?: 'guest-token') {
    return authMap.get(getKey(server, tokenType));
  },
  async _logonAsGuest(server: string) {
    try {
      await performGuestLogon(server);
    } catch (e) {
      updateAuthMapValue(server, 'guest', {
        status: 'initalizing',
        error: embeddedBundle['SASServer.logonError.msg'](),
      });
      throw new Error('[authorization] Unable to log in as guest.');
    }
    this.authorize(server, 'guest');
  },
  addConnection(server, authenticationType) {
    let authInfo = authMap.get(getKey(server, authenticationType));

    if (authenticationType === 'guest-token') {
      if (authInfo) {
        return authInfo;
      }
      authInfo = {
        status: 'initalizing',
        guestType: 'token',
      };
      setAuthMapValue(server, authenticationType, authInfo);

      initGuestToken(server);
      return authInfo;
    }

    if (authInfo) {
      if (authenticationType === 'guest') {
        if (authInfo.status === 'credentials-invalid') {
          updateAuthMapValue(server, 'guest', {
            status: 'initalizing',
            guestType: 'credential',
          });
          this._logonAsGuest(server);
        } else {
          updateAuthMapValue(server, 'guest', {
            guestType: 'credential',
          });
        }
      }
      return authInfo;
    }

    authInfo = {
      status: 'initalizing',
      guestType: authenticationType === 'guest' ? 'credential' : undefined,
    };
    setAuthMapValue(server, authenticationType, authInfo);

    (async () => {
      try {
        const resp = await fetch(getAuthUrl(server), { credentials: 'include' });
        const text = await resp.text();
        if (text.includes('data-report-sas-auth-success')) {
          updateAuthMapValue(server, 'credentials', {
            status: 'authorized',
          });
          updateUserId(server);
        } else {
          if (authMap.get(getKey(server, authenticationType))?.guestType === 'credential') {
            await this._logonAsGuest(server);
          } else {
            updateAuthMapValue(server, 'credentials', {
              status: 'credentials-invalid',
            });
          }
        }
      } catch (error) {
        if (authMap.get(getKey(server, authenticationType))?.guestType === 'credential') {
          // This may occur with custom SSO, try and make the sso_guest login since this bypasses
          //   custom SSO
          await this._logonAsGuest(server);
        } else {
          console.warn('[Authorization]: unable to determine auth status', error);
          updateAuthMapValue(server, 'credentials', {
            status: 'credentials-invalid',
          });
        }
      }
    })();

    return authInfo;
  },
  activate() {
    if (windowFocusCb) {
      return;
    }
    windowFocusCb = async () => {
      for (const [host, item] of authMap) {
        if (item.status !== 'authorized') {
          continue;
        }
        const reportsResp = await fetch(`${host}/reports/`, {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'include',
        });
        if (!reportsResp.ok || reportsResp.url.includes('/SASLogon/')) {
          authInterfaceImpl.deauthorize(host);
        }
      }
    };

    // @ts-ignore
    if (window.sas?.ltjs?.jest) {
      return;
    }
    window.addEventListener('focus', windowFocusCb);
  },
  deactivate() {
    if (windowFocusCb) {
      window.removeEventListener('focus', windowFocusCb);
      windowFocusCb = undefined;
    }
    statusCbs.clear();
    authMap.clear();
    userIdMap.clear();
  },
  deauthorize(server, authenticationType?: AuthenticationType) {
    const authInfo = authMap.get(getKey(server, authenticationType));
    if (!authInfo || authInfo.status !== 'authorized') {
      return;
    }
    if (authInfo.guestType === 'token') {
      updateAuthMapValue(server, 'guest-token', {
        status: 'initalizing',
        token: undefined,
      });
      initGuestToken(server);
      return;
    }
    if (!authInfo.guestType) {
      updateAuthMapValue(server, authenticationType, {
        status: 'credentials-invalid',
        serverHack: undefined,
      });
      return;
    }
    updateAuthMapValue(server, authenticationType, {
      status: 'initalizing',
      serverHack: undefined,
    });
    // timeout because we can can get a deauthorize call per 401 request result.
    setTimeout(async () => {
      const [token] = await getGuestToken(server);
      if (!token) {
        updateAuthMapValue(server, authenticationType, {
          status: 'credentials-invalid',
        });
        throw new Error('[authorization] unable to re-authorize guest.');
      }

      const userResp = await fetch(`${server}/identities/users/@currentUser`, {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          // eslint-disable-next-line @sas/i18n/jsx-to-localize
          Authorization: `bearer ${token}`,
        },
        credentials: 'omit',
      });
      if (!userResp.ok) {
        updateAuthMapValue(server, authenticationType, {
          status: 'credentials-invalid',
        });
        throw new Error('[authorization] unable to re-authorize guest.');
      }
      try {
        const json = await userResp.json();
        if (json.id !== userIdMap.get(server)) {
          throw new Error('[authorization] user is not guest.');
        }
        // We are guest!
        await this._logonAsGuest(server);
      } catch (e) {
        updateAuthMapValue(server, authenticationType, {
          status: 'credentials-invalid',
        });
        throw new Error('[authorization] unable to re-authorize guest.');
      }
    }, 15);
  },
  authorize(server: string, authenticationType?: AuthenticationType) {
    const info = authMap.get(getKey(server, authenticationType));
    if (info?.status === 'authorized' || info?.serverHack) {
      return;
    }
    if (authenticationType === 'guest-token') {
      updateAuthMapValue(server, authenticationType, {
        status: 'authorized',
      });
      return;
    }
    updateAuthMapValue(server, authenticationType, {
      serverHack: true,
      status: 'initalizing',
    });
    // Making multiple requests to an endpoint before it has gone through the authorization loop
    //  can cause problems due to a limitation in the Viya platform. To work around this, make the
    //  /reports/ and /identities/ requests before we are fully authorized.
    Promise.all([
      updateUserId(server),
      fetch(`${server}/reports/`, {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
      }).catch(() => {}),
    ]).then(() => {
      updateAuthMapValue(server, authenticationType, {
        status: 'authorized',
        serverHack: undefined,
      });
    });
  },
  _removeServer(server: string, authenticationType?: AuthenticationType) {
    if (authenticationType) {
      authMap.delete(getKey(server, authenticationType));
    } else {
      authMap.delete(server);
      userIdMap.delete(server);
    }
  },
  _getState() {
    return {
      statusCbs,
      authMap,
      userIdMap,
    };
  },
};

export const authInterface = authInterfaceImpl as globalInterface;
